/**
 * Pane Relief — Scroll-Reveal Animation System
 * IntersectionObserver-based entrance animations.
 * Respects prefers-reduced-motion automatically.
 *
 * Usage:
 *   data-reveal              → fade-up (default)
 *   data-reveal="fade"       → fade only (no translate)
 *   data-reveal="slide-left" → slide from left
 *   data-reveal="slide-right"→ slide from right
 *   data-reveal="scale"      → scale up from 0.92
 *   data-reveal="lift"       → lift with shadow (cards)
 *
 * Stagger children:
 *   data-reveal-stagger      → on parent, auto-staggers
 *                               direct children that have data-reveal
 *
 * Delay:
 *   data-reveal-delay="200"  → ms delay before animation
 *
 * Created: March 5, 2026
 */
;(function () {
  'use strict';

  /* ── Respect reduced-motion ── */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    /* Make everything visible immediately */
    var style = document.createElement('style');
    style.textContent = '[data-reveal]{opacity:1!important;transform:none!important}';
    document.head.appendChild(style);
    return;
  }

  /* ── Observer config ── */
  /* THRESHOLD is a fraction of the TARGET's own total area, not the
     viewport's — so it silently breaks for any [data-reveal] element
     taller than roughly (viewport height / THRESHOLD). At 0.12 that's
     ~6,800px on a typical mobile viewport; collection-intro's .ci-wrap
     (the whole rich content block below the product grid: Homeowner/
     Trade cards, spec finder, FAQ) runs ~8,300px tall there, so it can
     never reach 12% on-screen no matter how far you scroll — a mobile
     screen physically can't show that much of it at once. Result: that
     entire section stayed invisible (opacity: 0 from the base
     [data-reveal] rule) forever on mobile. Found 2026-09-10. 0 fires as
     soon as a single pixel is visible — standard for this UX pattern
     precisely because it doesn't have this failure mode, and doesn't
     meaningfully change how normal-sized cards feel (12% of a ~300px
     card is only ~35px anyway). */
  var THRESHOLD = 0;
  var ROOT_MARGIN = '0px 0px -40px 0px'; /* Trigger slightly before full viewport entry */

  /* ── Handle stagger parents ── */
  function applyStagger(parent) {
    var children = parent.querySelectorAll(':scope > [data-reveal], :scope > * > [data-reveal]');
    for (var i = 0; i < children.length; i++) {
      children[i].style.setProperty('--reveal-delay', (i * 100) + 'ms');
    }
  }

  /* ── Set up stagger parents ── */
  var staggerParents = document.querySelectorAll('[data-reveal-stagger]');
  for (var s = 0; s < staggerParents.length; s++) {
    applyStagger(staggerParents[s]);
  }

  /* ── Observer callback ── */
  function onIntersect(entries, observer) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        entries[i].target.classList.add('pr-revealed');
        observer.unobserve(entries[i].target);
      }
    }
  }

  var observer = new IntersectionObserver(onIntersect, {
    threshold: THRESHOLD,
    rootMargin: ROOT_MARGIN
  });

  /* ── Observe all reveal elements ── */
  var elements = document.querySelectorAll('[data-reveal]');
  for (var e = 0; e < elements.length; e++) {
    /* Apply explicit delay if set */
    var delay = elements[e].getAttribute('data-reveal-delay');
    if (delay) {
      elements[e].style.setProperty('--reveal-delay', delay + 'ms');
    }
    observer.observe(elements[e]);
  }

  /* ── Counter animation (for stats) ── */
  var counters = document.querySelectorAll('[data-count-to]');
  if (counters.length) {
    var counterObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          animateCounter(entries[i].target);
          counterObserver.unobserve(entries[i].target);
        }
      }
    }, { threshold: 0.3 });

    for (var c = 0; c < counters.length; c++) {
      counterObserver.observe(counters[c]);
    }
  }

  function animateCounter(el) {
    var raw = el.getAttribute('data-count-to');
    var suffix = el.getAttribute('data-count-suffix') || '';
    var prefix = el.getAttribute('data-count-prefix') || '';
    var target = parseFloat(raw.replace(/,/g, ''));
    var duration = 1600;
    var start = performance.now();

    function step(now) {
      var progress = Math.min((now - start) / duration, 1);
      /* Ease-out cubic */
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.round(eased * target);

      /* Format with commas */
      var formatted = current.toLocaleString('en-GB');
      el.textContent = prefix + formatted + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }

  /* ── Re-observe dynamically added elements (Shopify section rendering) ── */
  document.addEventListener('shopify:section:load', function () {
    var newElements = document.querySelectorAll('[data-reveal]:not(.pr-revealed)');
    for (var n = 0; n < newElements.length; n++) {
      observer.observe(newElements[n]);
    }
    var newStagger = document.querySelectorAll('[data-reveal-stagger]');
    for (var ns = 0; ns < newStagger.length; ns++) {
      applyStagger(newStagger[ns]);
    }
  });
})();
